/** @jsx jsx */
import { useFileboy } from "@cd2/fileboy-react"
import { css, jsx } from "@emotion/core"
import { useState } from "react"
import { useAsyncEffect } from "../../../hooks/useAsyncEffect"
import { fileTypes } from "../../../types/common"
import { useSettings } from "../../../utils/useSettings"

export const placeholderImageId = "placeholderProduct"
export interface IProductThumbnail {
  id: string
  brand: {
    id: string
    fileboyLogoId: string
  } | null
  thumbnailImage: {
    id: string
    fileboyId: string
  } | null
}

function pxOrStr(value: string | number) {
  return typeof value === "number" ? `${value}px` : value
}

export function ProductMasterThumbnail({
  productMasterId,
  className,
  productId,
  fileboyImageTransform,
  imageSize,
  imageId,
  borderRadius = 4,
  imageSrc,
}: {
  className?: string
  productMasterId?: string
  productId?: string
  imageId?: string
  fileboyImageTransform?: { height: number; width?: never } | { width: number; height?: never }
  imageSize?:
    | { size: number | string }
    | { width?: number | string; height?: number | string; size?: never }
  borderRadius?: number
  imageSrc?: string | null
}) {
  const { productImageFallbackFileboyId } = useSettings()
  const fileboy = useFileboy()
  const skeem = window.skeem
  const [thumbnailData, setThumbnailData] = useState<IProductThumbnail | null>(null)

  useAsyncEffect(async () => {
    try {
      if (imageSrc && imageSrc.length > 0) {
        return
      }
      if (productMasterId) {
        const record = await skeem.fetch2<IProductThumbnail>({
          first: {
            name: "product_masters",
            attributes: [
              {
                as: "thumbnailImage",
                value: {
                  first: {
                    attr: {
                      name: "files",
                      attributes: ["fileboyId"],
                      sort: { by: "weight", direction: "asc" },
                      filter: {
                        and: [
                          { matches: { fileType: fileTypes.image } },
                          imageId && { matches: { id: imageId } },
                        ].filter(Boolean),
                      },
                    },
                  },
                },
              },
              { name: "brand", attributes: ["fileboyLogoId"] },
            ],
            filter: { matches: { id: productMasterId } },
          },
        })
        setThumbnailData(record)
      } else if (productId) {
        const record = await skeem.fetch2<IProductThumbnail>({
          first: {
            name: "products",
            attributes: [
              {
                as: "thumbnailImage",
                value: {
                  first: {
                    path: [
                      "product_master",
                      {
                        name: "files",
                        attributes: ["fileboyId"],
                        sort: { by: "weight", direction: "asc" },
                        filter: { matches: { fileType: fileTypes.image } },
                      },
                    ],
                  },
                },
              },
              {
                as: "brand",
                value: {
                  path: ["product_master", { name: "brand", attributes: ["fileboyLogoId"] }],
                },
              },
            ],
            filter: { matches: { id: productId } },
          },
        })
        setThumbnailData(record)
      }
    } catch (e) {
      console.error("Error loading product thumbnail", e)
    }
  }, [productId, productMasterId, imageId])

  const imageSource =
    thumbnailData?.thumbnailImage?.fileboyId ||
    productImageFallbackFileboyId ||
    thumbnailData?.brand?.fileboyLogoId ||
    placeholderImageId

  const isPlaceholder =
    imageSource === placeholderImageId ||
    (productImageFallbackFileboyId && imageSource === productImageFallbackFileboyId)

  const imageUrl =
    imageSrc ||
    fileboy.buildImageUrl(imageSource, {
      transforms: [
        {
          type: "resize",
          height: fileboyImageTransform ? fileboyImageTransform?.height : 160,
          width: fileboyImageTransform?.width,
        },
      ],
    })

  const width = imageSize?.size ?? imageSize?.width ?? 44
  const height = imageSize?.size ?? imageSize?.height ?? 44
  return (
    <div
      css={css`
        padding: ${!isPlaceholder ? 0 : 6}px;
        height: ${pxOrStr(height)};
        width: ${pxOrStr(width)};
        background-color: white;
        border-radius: ${borderRadius}px;
        overflow: hidden;
      `}
    >
      <div
        css={css`
          background-image: url(${imageUrl});
          background-size: contain;
          background-repeat: no-repeat;
          background-position: 50%;
          height: 100%;
        `}
        className={className}
      />
    </div>
  )
}
